import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Bell } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api, formatApiErrorDetail } from "../lib/auth";
import { toast } from "sonner";
import { PUBLIC_EVENTS } from "../constants/testIds";

// CONTENT PROVENANCE NOTE
// -----------------------------------------------------------------------------
// Per Phase 1 Decision 5:
// /portal/events is authenticated data and MUST NOT be reused, re-labelled or
// de-identified for public consumption. No genuinely public event source
// exists yet, so this page ships with an honest empty state and a newsletter
// subscribe pathway. DO NOT populate this page from private data.
// -----------------------------------------------------------------------------

export default function EventsPublic() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/forms/newsletter", { email });
      toast.success("You're on the list. We'll email you when public events are scheduled.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not subscribe.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={PUBLIC_EVENTS.page} className="bg-white">
      <Header />
      <main id="main-content">
        {/* Hero */}
        <section
          data-testid={PUBLIC_EVENTS.hero}
          className="pt-28 lg:pt-32 pb-14 lg:pb-20 bg-white"
        >
          <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-12">
            <p className="btc-eyebrow mb-4">EVENTS</p>
            <h1 className="font-display font-extrabold tracking-tight leading-[1.05] text-[40px] sm:text-[52px] lg:text-[64px] text-[#0B132B] mb-6">
              Come <span className="text-[#23803F]">together.</span>
            </h1>
            <div className="h-[3px] w-16 bg-[#2D9B4E] rounded-full mb-6" />
            <p className="text-lg sm:text-xl text-[#4B5563] leading-relaxed max-w-3xl">
              Public BTC events — workshops, talks, community gatherings and fundraisers — will be
              published here. Subscribe below to be the first to know when public events open.
            </p>
          </div>
        </section>

        {/* Empty state */}
        <section data-testid={PUBLIC_EVENTS.list} className="bg-[#F8F9FB] py-20 lg:py-28">
          <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-12">
            <div
              data-testid={PUBLIC_EVENTS.emptyState}
              className="bg-white rounded-3xl border border-gray-100 p-10 lg:p-14 text-center"
            >
              <div className="w-16 h-16 rounded-full bg-[#1A5FAB]/10 flex items-center justify-center mx-auto mb-6">
                <Calendar className="w-7 h-7 text-[#1A5FAB]" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-[#0B132B] tracking-tight mb-4">
                No public events scheduled yet.
              </h2>
              <p className="text-[#4B5563] leading-relaxed max-w-xl mx-auto mb-8">
                We are preparing our first public BTC events. Leave your email and we will let you
                know the moment one is announced — no other emails, no spam.
              </p>
              <form
                onSubmit={subscribe}
                className="max-w-md mx-auto flex flex-col sm:flex-row gap-3"
              >
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 px-5 py-3 rounded-full bg-[#F8F9FB] border border-gray-200 focus:border-[#2D9B4E] focus:outline-none focus:ring-2 focus:ring-[#2D9B4E]/20 text-[#0B132B] placeholder:text-gray-400"
                />
                <button
                  type="submit"
                  data-testid={PUBLIC_EVENTS.notifyCta}
                  disabled={submitting}
                  className="btc-btn-primary justify-center disabled:opacity-60"
                >
                  {submitting ? "Subscribing..." : (
                    <>
                      Notify me
                      <Bell className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* Alt paths */}
        <section className="bg-white py-16 lg:py-20">
          <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-12 text-center">
            <p className="text-[#4B5563] mb-6">
              Looking for support today? BTC is here whether or not an event is scheduled.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/support" className="btc-btn-primary justify-center">
                GET SUPPORT
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/get-involved" className="btc-btn-outline justify-center">
                Get Involved
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
